var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, FormHelperText, IconButton } from '@mui/material';
import { validateAge } from 'hx-util';
import { useEffect, useMemo, useRef } from 'react';
import { useController, useFormContext, } from 'react-hook-form';
import chevronLeft from '../../assets/chevron-left.svg';
import chevronRight from '../../assets/chevron-right.svg';
var DEFAULT_MIN_AGE = 18;
var DEFAULT_MIN_YEAR = 1900;
var DEFAULT_VISIBLE_YEARS = 5;
var SWIPE_THRESHOLD = 24;
function getWindowYears(_a) {
    var selectedYear = _a.selectedYear, minYear = _a.minYear, maxYear = _a.maxYear, visibleYears = _a.visibleYears;
    var half = Math.floor(visibleYears / 2);
    var start = selectedYear - half;
    var end = selectedYear + half;
    if (start < minYear) {
        end += minYear - start;
        start = minYear;
    }
    if (end > maxYear) {
        start -= end - maxYear;
        end = maxYear;
    }
    start = Math.max(start, minYear);
    end = Math.min(end, maxYear);
    var years = [];
    for (var year = start; year <= end; year += 1) {
        years.push(year);
    }
    return years;
}
export var BirthYearPicker = function (_a) {
    var _b = _a.minAge, minAge = _b === void 0 ? DEFAULT_MIN_AGE : _b, _c = _a.pickerProps, _d = _c === void 0 ? {} : _c, _e = _d.name, name = _e === void 0 ? 'birth-year' : _e, _f = _d.className, className = _f === void 0 ? '' : _f, sx = _d.sx, showEighteenPlusYearsOnly = _d.showEighteenPlusYearsOnly, _g = _d.minYear, minYear = _g === void 0 ? DEFAULT_MIN_YEAR : _g, _h = _d.visibleYears, visibleYearsCount = _h === void 0 ? DEFAULT_VISIBLE_YEARS : _h, _j = _d.ariaLabel, ariaLabel = _j === void 0 ? 'Birth year' : _j, _k = _d.getSelectYearAriaLabel, getSelectYearAriaLabel = _k === void 0 ? function (year) { return "Select year ".concat(year); } : _k, _l = _d.leftChevronAriaLabel, leftChevronAriaLabel = _l === void 0 ? 'Previous year' : _l, _m = _d.rightChevronAriaLabel, rightChevronAriaLabel = _m === void 0 ? 'Next year' : _m, _o = _d.rules, rules = _o === void 0 ? {
        required: 'Veuillez sélectionner votre année de naissance.',
        validate: function (value) {
            return validateAge(Number(value), minAge)
                ? undefined
                : "Vous devez avoir au moins ".concat(minAge, " ans.");
        },
    } : _o;
    var _p = useFormContext(), setError = _p.setError, clearErrors = _p.clearErrors;
    var currentYear = new Date().getFullYear();
    var maxYear = currentYear;
    var defaultSelectedYear = Math.max(minYear, showEighteenPlusYearsOnly ? currentYear - DEFAULT_MIN_AGE : currentYear);
    var normalizedVisibleYears = visibleYearsCount % 2 === 0 ? visibleYearsCount + 1 : visibleYearsCount;
    var _q = useController({
        name: name,
        rules: rules,
        defaultValue: '',
    }), _r = _q.field, value = _r.value, onChange = _r.onChange, fieldName = _r.name, error = _q.fieldState.error;
    useEffect(function () {
        if (value)
            return;
        onChange(String(defaultSelectedYear));
    }, [defaultSelectedYear, onChange, value]);
    var selectedYear = useMemo(function () {
        var numericValue = Number(value);
        if (Number.isNaN(numericValue))
            return maxYear;
        return Math.min(maxYear, Math.max(minYear, numericValue));
    }, [maxYear, minYear, value]);
    var yearWindow = useMemo(function () {
        return getWindowYears({
            selectedYear: selectedYear,
            minYear: minYear,
            maxYear: maxYear,
            visibleYears: normalizedVisibleYears,
        });
    }, [maxYear, minYear, normalizedVisibleYears, selectedYear]);
    var selectedIndex = yearWindow.findIndex(function (windowYear) { return windowYear === selectedYear; });
    var pointerStartX = useRef(null);
    var setYear = function (nextYear) {
        var boundedYear = Math.min(maxYear, Math.max(minYear, nextYear));
        onChange(String(boundedYear));
        if (showEighteenPlusYearsOnly && currentYear - boundedYear < minAge) {
            setError(name, {
                type: 'validate',
                message: "Vous devez avoir au moins ".concat(minAge, " ans."),
            });
            return;
        }
        clearErrors(name);
    };
    var onPointerDown = function (event) {
        pointerStartX.current = event.clientX;
    };
    var onPointerUp = function (event) {
        if (pointerStartX.current === null)
            return;
        var delta = event.clientX - pointerStartX.current;
        pointerStartX.current = null;
        if (Math.abs(delta) < SWIPE_THRESHOLD)
            return;
        if (delta > 0) {
            setYear(selectedYear - 1);
            return;
        }
        setYear(selectedYear + 1);
    };
    var onKeyDown = function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
            return;
        event.preventDefault();
        if (event.key === 'ArrowLeft') {
            setYear(selectedYear - 1);
            return;
        }
        setYear(selectedYear + 1);
    };
    return (_jsx(_Fragment, { children: _jsxs(Box, __assign({ className: className, tabIndex: 0, role: "spinbutton", "aria-valuemin": minYear, "aria-valuemax": maxYear, "aria-valuenow": selectedYear, "aria-label": ariaLabel, onKeyDown: onKeyDown, onPointerDown: onPointerDown, onPointerUp: onPointerUp, sx: __spreadArray([
                {
                    width: '100%',
                    marginInline: 'auto',
                    outline: 'none',
                    userSelect: 'none',
                    touchAction: 'pan-y',
                    containerType: 'inline-size',
                    '& [data-year-row]': {
                        display: 'grid',
                        gridTemplateColumns: "repeat(".concat(yearWindow.length, ", minmax(0, 1fr))"),
                        alignItems: 'center',
                        width: '100%',
                    },
                    '& [data-year-item]': {
                        border: 'none',
                        background: 'transparent',
                        width: '100%',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 700,
                        fontSize: '2rem',
                        lineHeight: 1.1,
                        padding: 0,
                        cursor: 'pointer',
                        '@supports (font-size: 1cqw)': {
                            fontSize: 'clamp(1.1rem, 6cqw, 2rem)',
                        },
                    },
                    '& [data-year-item="selected"]': {
                        color: 'text.primary',
                    },
                    '& [data-year-item="unselected"][data-year-distance="1"]': {
                        color: 'rgba(255, 255, 255, 0.13)',
                    },
                    '& [data-year-item="unselected"][data-year-distance="2"]': {
                        color: 'rgba(255, 255, 255, 0.05)',
                    },
                    '& [data-selected-content]': {
                        position: 'relative',
                        display: 'grid',
                        placeItems: 'center',
                        width: '100%',
                        minHeight: { xs: '2rem', md: '2.25rem' },
                    },
                    '& [data-year-label="selected"]': {
                        position: 'relative',
                        minWidth: '3ch',
                        textAlign: 'center',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: '-1rem',
                            marginInline: 'auto',
                            width: '100%',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                        },
                    },
                    '& [data-chevron]': {
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'primary.main',
                        fontSize: 'inherit',
                        padding: 0,
                        width: '1em',
                        height: '1em',
                        minWidth: '1em',
                        borderRadius: '9999px',
                    },
                    '& [data-chevron-icon]': {
                        width: '0.45em',
                        height: '0.675em',
                        display: 'block',
                    },
                    '& [data-chevron="left"]': {
                        left: '50%',
                        transform: 'translate(calc(-100% - 1.5em), -50%)',
                    },
                    '& [data-chevron="right"]': {
                        left: '50%',
                        transform: 'translate(1.5em, -50%)',
                    },
                }
            ], (Array.isArray(sx) ? sx : [sx]), true) }, { children: [_jsx(Box, __assign({ "data-year-row": true }, { children: yearWindow.map(function (year, index) {
                        var isSelected = year === selectedYear;
                        var distanceFromSelected = Math.abs(index - selectedIndex);
                        return (_jsx("button", __assign({ type: "button", "data-year-item": isSelected ? 'selected' : 'unselected', "data-year-distance": distanceFromSelected, onClick: function () { return setYear(year); }, "aria-label": getSelectYearAriaLabel(year), "aria-current": isSelected ? 'true' : undefined }, { children: isSelected ? (_jsxs(Box, __assign({ "data-selected-content": true }, { children: [_jsx(IconButton, __assign({ "aria-label": leftChevronAriaLabel, "data-chevron": "left", onClick: function (event) {
                                            event.stopPropagation();
                                            setYear(selectedYear - 1);
                                        }, disabled: selectedYear <= minYear }, { children: _jsx(Box, { component: "img", src: chevronLeft, alt: "", "aria-hidden": true, "data-chevron-icon": true }) })), _jsx(Box, __assign({ component: "span", "data-year-label": "selected", sx: { fontSize: 'inherit', fontWeight: 'inherit' } }, { children: year })), _jsx(IconButton, __assign({ "aria-label": rightChevronAriaLabel, "data-chevron": "right", onClick: function (event) {
                                            event.stopPropagation();
                                            setYear(selectedYear + 1);
                                        }, disabled: selectedYear >= maxYear }, { children: _jsx(Box, { component: "img", src: chevronRight, alt: "", "aria-hidden": true, "data-chevron-icon": true }) }))] }))) : (_jsx(Box, __assign({ component: "span", sx: { fontSize: 'inherit', fontWeight: 'inherit' } }, { children: year }))) }), "".concat(fieldName, "-year-").concat(year)));
                    }) })), _jsx(FormHelperText, __assign({ error: Boolean(error), sx: {
                        minHeight: '1.5rem',
                        mt: '20px',
                        visibility: error ? 'visible' : 'hidden',
                    } }, { children: (error === null || error === void 0 ? void 0 : error.message) || ' ' }))] })) }));
};
